import { useToast } from "@design";
import { useTranslation } from "react-i18next";
import type { AttachmentLimitsResponse } from "@/api";
import { formatBytes, formatList } from "@/i18n/format";
import type { AttachmentKind, AttachmentRejection } from "./attachmentRules";

const KINDS: AttachmentKind[] = ["document", "image"];

/** "PDF, DOCX, …, PNG": the extensions the backend accepts, as people know them. */
const typeLabels = (limits: AttachmentLimitsResponse) => [
  ...new Set(KINDS.flatMap((kind) => limits[kind].extensions.map((ext) => ext.replace(/^\./, "").toUpperCase()))),
];

/**
 * Explains files that weren't attached, in one error toast: which files (title) and why (description), e.g.
 * "deck.pptx is 76 MB. The limit for documents is 50 MB."
 */
export function useRejectionToast(limits: AttachmentLimitsResponse | undefined) {
  const { t } = useTranslation("chat", { keyPrefix: "attachments" });
  const toast = useToast();

  const reasons = (rejections: AttachmentRejection[]) => {
    const parts: string[] = [];
    const unsupported: string[] = [];
    for (const rejection of rejections) {
      if (rejection.reason === "tooLarge") {
        const { name, kind, size, maxSize } = rejection;
        parts.push(t(`tooLarge.${kind}`, { name, size: formatBytes(size), limit: formatBytes(maxSize) }));
      } else if (rejection.reason === "unsupported") unsupported.push(rejection.name);
      else parts.push(t("tooMany", { max: rejection.max }));
    }
    if (unsupported.length > 0) {
      parts.push(t("unsupported", { count: unsupported.length, names: formatList(unsupported) }));
      if (limits) parts.push(t("supportedTypes", { types: formatList(typeLabels(limits)) }));
    }
    return parts.join(" ");
  };

  /** `notAttached`: every file left out, when more than the rejected ones (a failed upload drops its whole batch). */
  return (
    rejections: AttachmentRejection[],
    notAttached = rejections.flatMap((r) => ("name" in r ? [r.name] : [])),
  ) => {
    if (rejections.length === 0) return;
    toast.error(t("notAttached", { count: notAttached.length, name: notAttached[0] }), {
      description: reasons(rejections),
    });
  };
}
